'use client';

import React, { Fragment, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import Header from '../../components/Header';
import Load from '../../components/Load';
import TournamentDiscountItem from './discount/components/TournamentDiscountItem';
import {
  DESCONTO_CATEGORIAS_POR_TORNEIO,
  DescontosCategoriaPorTorneioData,
  DescontosCategoriaPorTorneioVars,
  adicionarDescontoCategoria,
  atualizarDescontoCategoria,
  removerDescontoCategoria,
  DescontoCategoria,
} from '../../services/descontoTorneioService';
import {
  TORNEIO_POR_ID,
  TorneioPorIdData,
  TorneioPorIdVars,
} from '../../services/torneioService';

interface DiscountItem extends DescontoCategoria {
  tempId: string;
}

const AdminTournamentDiscountPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const torneioId = searchParams.get('torneioId');

  const { data: torneioData, loading: loadingTorneio } = useQuery<
    TorneioPorIdData,
    TorneioPorIdVars
  >(TORNEIO_POR_ID, {
    variables: { id: torneioId || '' },
    skip: !torneioId,
  });

  const {
    data: descontosData,
    loading: loadingDescontos,
    refetch,
  } = useQuery<DescontosCategoriaPorTorneioData, DescontosCategoriaPorTorneioVars>(
    DESCONTO_CATEGORIAS_POR_TORNEIO,
    {
      variables: { torneioId: torneioId || '' },
      skip: !torneioId,
    },
  );

  const [items, setItems] = useState<DiscountItem[]>([]);
  const [dirtyMap, setDirtyMap] = useState<Record<string, boolean>>({});
  const [deletedIds, setDeletedIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (descontosData?.descontosCategoriaPorTorneio) {
      const mapped = descontosData.descontosCategoriaPorTorneio.map((d) => ({
        ...d,
        tempId: d.id ?? `temp-${Math.random().toString(36).slice(2, 11)}`,
      }));
      setItems(mapped);
    } else {
      setItems([]);
    }
  }, [descontosData]);

  /**
   * Recalcula a ordem dos itens após remoção, garantindo sequência contínua
   * @param list - Lista de itens para recalcular ordem
   * @returns Lista com ordens recalculadas (1, 2, 3, ...)
   */
  const recomputeOrder = (list: DiscountItem[]) =>
    list.map((item, index) => ({ ...item, ordem: index + 1 }));

  /**
   * Adiciona um novo desconto temporário à lista
   * O item será marcado como "dirty" e será criado no backend quando o usuário salvar
   */
  const handleAdd = () => {
    const tempId = `temp-${Date.now()}`;
    const ordem = items.length + 1;
    const newItem: DiscountItem = {
      tempId,
      torneioId: torneioId || '',
      tipo: 'PERCENTUAL',
      valor: 0,
      ordem,
    };
    setItems((prev) => [...prev, newItem]);
    setDirtyMap((prev) => ({ ...prev, [tempId]: true }));
  };

  /**
   * Marca um item como modificado (dirty) para indicar que precisa ser salvo
   * @param tempId - ID temporário do item
   */
  const markDirty = (tempId: string) =>
    setDirtyMap((prev) => ({ ...prev, [tempId]: true }));

  /**
   * Atualiza um item localmente e marca como modificado
   * @param tempId - ID temporário do item
   * @param patch - Objeto com campos a atualizar
   */
  const handleItemChange = (
    tempId: string,
    patch: Partial<DescontoCategoria>,
  ) => {
    setItems((prev) =>
      prev.map((item) =>
        item.tempId === tempId ? { ...item, ...patch } : item,
      ),
    );
    markDirty(tempId);
  };

  /**
   * Remove um item da lista após confirmação
   * Se o item já existe no backend (tem id), será adicionado à lista de deleções
   * A ordem dos itens restantes será recalculada automaticamente
   * @param item - Item a ser removido
   */
  const handleDelete = (item: DiscountItem) => {
    if (!window.confirm('Remover desconto?')) return;
    setItems((prev) => recomputeOrder(prev.filter((p) => p.tempId !== item.tempId)));
    if (item.id) {
      setDeletedIds((prev) => [...prev, item.id!]);
    }
  };

  /**
   * Valida os dados dos descontos antes do salvamento
   * @param items - Lista de itens a validar
   * @returns Array de mensagens de erro (vazio se não houver erros)
   */
  const validateItems = (items: DiscountItem[]): string[] => {
    const errors: string[] = [];
    
    items.forEach((item, index) => {
      if (item.valor < 0) {
        errors.push(`Desconto ${index + 1}: valor não pode ser negativo`);
      }
      if (item.valor === 0) {
        errors.push(`Desconto ${index + 1}: valor deve ser maior que zero`);
      }
      if (item.tipo !== 'PERCENTUAL' && item.tipo !== 'FIXO') {
        errors.push(`Desconto ${index + 1}: tipo inválido`);
      }
      if (item.ordem < 1) {
        errors.push(`Desconto ${index + 1}: ordem deve ser maior que zero`);
      }
      if (!Number.isFinite(item.valor)) {
        errors.push(`Desconto ${index + 1}: valor deve ser um número válido`);
      }
    });
    
    return errors;
  };

  /**
   * Salva todas as alterações de descontos em lote (criações, atualizações e deleções)
   * Processa as operações sequencialmente e rastreia sucessos e falhas
   */
  const handleSave = async () => {
    if (!torneioId) return;
    
    // Validação antes do salvamento
    const validationErrors = validateItems(items);
    if (validationErrors.length > 0) {
      alert(`Erros de validação:\n${validationErrors.join('\n')}`);
      return;
    }
    
    setSaving(true);
    const results = {
      created: 0,
      updated: 0,
      deleted: 0,
      errors: [] as string[],
    };
    
    try {
      const toCreate = items.filter((i) => !i.id);
      const toUpdate = items.filter((i) => i.id && dirtyMap[i.tempId]);

      // Processar criações
      for (const item of toCreate) {
        try {
          await adicionarDescontoCategoria({
            torneioId: torneioId,
            tipo: item.tipo,
            valor: item.valor,
            ordem: item.ordem,
          });
          results.created++;
        } catch (e: any) {
          const errorMsg = `Erro ao criar desconto (ordem ${item.ordem}): ${e?.message || 'Erro desconhecido'}`;
          results.errors.push(errorMsg);
        }
      }

      // Processar atualizações
      for (const item of toUpdate) {
        try {
          await atualizarDescontoCategoria({
            id: item.id!,
            input: {
              torneioId: torneioId,
              tipo: item.tipo,
              valor: item.valor,
              ordem: item.ordem,
            },
          });
          results.updated++;
        } catch (e: any) {
          const errorMsg = `Erro ao atualizar desconto (ordem ${item.ordem}): ${e?.message || 'Erro desconhecido'}`;
          results.errors.push(errorMsg);
        }
      }

      // Processar deleções
      for (const delId of deletedIds) {
        try {
          await removerDescontoCategoria(delId);
          results.deleted++;
        } catch (e: any) {
          const errorMsg = `Erro ao deletar desconto: ${e?.message || 'Erro desconhecido'}`;
          results.errors.push(errorMsg);
        }
      }

      // Limpar estados apenas se não houver erros
      if (results.errors.length === 0) {
        setDirtyMap({});
        setDeletedIds([]);
        await refetch();
      }

      // Mostrar resultado
      if (results.errors.length > 0) {
        const summary = [
          `Criados: ${results.created}`,
          `Atualizados: ${results.updated}`,
          `Deletados: ${results.deleted}`,
          `Erros: ${results.errors.length}`,
        ].join(' | ');
        alert(`${summary}\n\n${results.errors.join('\n')}`);
      } else {
        alert(`Descontos salvos com sucesso (${results.created} criados, ${results.updated} atualizados, ${results.deleted} deletados)`);
      }
    } catch (e: any) {
      alert(`Erro inesperado ao salvar descontos: ${e?.message || 'Erro desconhecido'}`);
    } finally {
      setSaving(false);
    }
  };

  if (loadingTorneio) return <Load />;

  const torneio = torneioData?.torneioPorId;

  return (
    <Fragment>
      <div className="main-wrapper">
        <Header />
        <div className="main-content theme-dark-bg right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="middle-wrap">
                <div className="card w-100 border-0 bg-white shadow-xs p-0 mb-4">
                  <div className="card-body p-4 w-100 bg-current border-0 d-flex rounded-3 align-items-center">
                    <button
                      type="button"
                      className="btn btn-link text-white p-0 d-inline-block mt-2 me-4"
                      onClick={() => window.history.back()}
                    >
                      <i className="ti-arrow-left font-sm"></i>
                    </button>
                    <h4 className="font-xs text-white fw-600 mb-0 mt-2 flex-grow-1">
                      {torneio?.nome || 'Torneio'}
                    </h4>
                    <button
                      className="btn btn-primary"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? 'SALVANDO...' : 'SALVAR ALTERAÇÕES'}
                    </button>
                  </div>
                </div>

                <div className="mb-3">
                  {torneio?.bannerUrl && (
                    <img
                      src={torneio.bannerUrl}
                      alt="banner"
                      className="w-100 rounded"
                      style={{ maxHeight: 200, objectFit: 'cover' }}
                    />
                  )}
                </div>

                <div className="d-flex justify-content-end mb-3">
                  <button className="btn btn-secondary" onClick={handleAdd}>
                    Adicionar desconto +
                  </button>
                </div>

                {loadingDescontos && <Load />}

                <div className="row g-3">
                  {items.map((item) => (
                    <div key={item.tempId} className="col-12">
                      <TournamentDiscountItem
                        value={item}
                        onChange={(patch) => handleItemChange(item.tempId, patch)}
                        onDelete={() => handleDelete(item)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AdminTournamentDiscountPage;

